import { keepPreviousData, useQuery, type QueryKey } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from './lib/api-client.js';
import { COMPARISON_SELECTION_CHANGE_EVENT, getComparisonCount } from './lib/comparison-selection.js';

interface QueryUpdateOptions { replace?: boolean; resetPage?: boolean }

export function useUrlFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => new URLSearchParams(searchParams), [searchParams]);
  const updateMany = useCallback((mutate: (next: URLSearchParams) => void, options: QueryUpdateOptions = {}) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      mutate(next);
      if (options.resetPage !== false) next.delete('page');
      return next;
    }, options.replace === undefined ? {} : { replace: options.replace });
  }, [setSearchParams]);
  const setValue = useCallback((key: string, value: string, options: QueryUpdateOptions = {}) => {
    updateMany((next) => {
      if (value) next.set(key, value); else next.delete(key);
    }, options);
  }, [updateMany]);
  return { params, setValue, updateMany };
}

export function useApiQuery<T>(key: QueryKey, path: string, enabled = true, options: { staleTime?: number; refetchInterval?: number | false; keepPreviousData?: boolean } = {}) {
  return useQuery({ queryKey: key, queryFn: () => api<T>(path), enabled, staleTime: options.staleTime ?? 30_000, ...(options.refetchInterval !== undefined ? { refetchInterval: options.refetchInterval } : {}), ...(options.keepPreviousData ? { placeholderData: keepPreviousData } : {}), refetchOnWindowFocus: false });
}

export function useQueryParam(name: string, fallback = ''): [string, (value: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const current = searchParams.get(name) ?? fallback;
  const setValue = useCallback((value: string) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value) next.set(name, value); else next.delete(name);
      next.delete('page');
      return next;
    }, { replace: true });
  }, [name, setSearchParams]);
  return [current, setValue];
}

export function useComparisonCount(pathname: string, search: string): number {
  const [count, setCount] = useState(() => getComparisonCount(pathname, search));

  useEffect(() => {
    const updateCount = () => setCount(getComparisonCount(pathname, search));
    updateCount();
    window.addEventListener(COMPARISON_SELECTION_CHANGE_EVENT, updateCount);
    window.addEventListener('storage', updateCount);
    return () => {
      window.removeEventListener(COMPARISON_SELECTION_CHANGE_EVENT, updateCount);
      window.removeEventListener('storage', updateCount);
    };
  }, [pathname, search]);

  return count;
}

